import type { ProjectNodeInputs } from "@oblique/shared/runtime"
import { useEffect, useState } from "react"

export const MAX_IMAGE_NODE_FILE_SIZE = 10 * 1024 * 1024
export const MAX_IMAGE_NODE_WIDTH = 520
export const MAX_IMAGE_NODE_HEIGHT = 420
export const imageNodeClassName = "nowheel block object-contain"

export const imageNodeCode = `import React from "react"

const ImageNode = ({ image, name }: { image?: string; name?: string }) => (
	<img
		src={image}
		alt={name ?? "Dropped image"}
		style={{ display: "block", maxHeight: 420, maxWidth: 520, objectFit: "contain" }}
	/>
)

export default ImageNode
`

export const isImageFile = (file: Pick<File, "name" | "type">) =>
	file.type.startsWith("image/") ||
	(!file.type && /\.(avif|bmp|gif|jpe?g|png|svg|tiff?|webp)$/i.test(file.name))

export const imageNodeSource = (
	metadata: Record<string, unknown>,
	inputs: ProjectNodeInputs,
) => {
	if (metadata.built_in_kind !== "image") return undefined
	const value = inputs.image?.value
	return typeof value === "string" && value.startsWith("data:image/")
		? value
		: undefined
}

export const imageNodeOutput = (
	metadata: Record<string, unknown>,
	inputs: ProjectNodeInputs,
) => {
	const image = imageNodeSource(metadata, inputs)
	return image ? { image } : undefined
}

export const readImageFileAsDataURL = (file: File) =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader()
		reader.onerror = () =>
			reject(reader.error ?? new Error("Image read failed"))
		reader.onload = () => {
			if (typeof reader.result === "string") {
				resolve(reader.result)
				return
			}
			reject(new Error("Image read failed"))
		}
		reader.readAsDataURL(file)
	})

export const imageNodeLabel = (fileName: string) => {
	const name = fileName.replace(/\.[^.]+$/, "").trim()
	return name || "Image"
}

export const fitImageNodeDimensions = (width: number, height: number) => {
	if (
		!Number.isFinite(width) ||
		!Number.isFinite(height) ||
		width <= 0 ||
		height <= 0
	) {
		return undefined
	}
	const scale = Math.min(
		1,
		MAX_IMAGE_NODE_WIDTH / width,
		MAX_IMAGE_NODE_HEIGHT / height,
	)
	return {
		height: Math.max(1, Math.round(height * scale)),
		width: Math.max(1, Math.round(width * scale)),
	}
}

export const ImageNode = ({
	source,
	label,
	onDimensionsChange,
}: {
	source: string
	label: string
	onDimensionsChange: (dimensions: { width: number; height: number }) => void
}) => {
	const [dimensions, setDimensions] = useState<
		{ width: number; height: number } | undefined
	>()

	useEffect(() => {
		if (!dimensions) return
		const frame = requestAnimationFrame(() => onDimensionsChange(dimensions))
		return () => cancelAnimationFrame(frame)
	}, [dimensions, onDimensionsChange])

	return (
		<img
			src={source}
			alt={label}
			draggable={false}
			className={imageNodeClassName}
			style={dimensions}
			onLoad={(event) => {
				setDimensions(
					fitImageNodeDimensions(
						event.currentTarget.naturalWidth,
						event.currentTarget.naturalHeight,
					),
				)
			}}
		/>
	)
}
